import axios from 'axios'
import { Message } from 'element-ui'
import config from '@/utils/config'
import { getToken, removeToken, refreshToken } from './auth'

// 创建 axios 实例
const service = axios.create({
    baseURL: config.baseURL, // API 基础路径
    timeout: config.timeout, // 请求超时时间
    headers: {
        'Content-Type': 'application/json;charset=UTF-8'
    }
})

// 请求频率限制
const requestLimit = {
    // 存储请求记录
    records: {},
    // 限制时间窗口（毫秒）
    windowMs: 1000,
    // 时间窗口内最大请求数
    maxRequests: 10,
    
    // 检查是否超过限制
    check(url) {
        const now = Date.now()
        const key = url
        
        if (!this.records[key]) {
            this.records[key] = []
        }
        
        // 清理过期记录
        this.records[key] = this.records[key].filter(timestamp => now - timestamp < this.windowMs)
        
        // 检查是否超过限制
        if (this.records[key].length >= this.maxRequests) {
            return false
        }
        
        // 记录本次请求
        this.records[key].push(now)
        return true
    }
}

// 请求拦截器
service.interceptors.request.use(
    config => {
        // 检查请求频率限制
        if (!requestLimit.check(config.url)) {
            return Promise.reject(new Error('请求过于频繁，请稍后再试'))
        }
        
        // 从自定义配置中获取是否需要 token，默认为 true
        const needToken = config.token !== false

        // 如果需要 token，则添加到请求头
        if (needToken) {
            const token = getToken()
            if (token) {
                config.headers['Authorization'] = token
            }
        }

        return config
    },
    error => {
        console.warn('请求错误')
        return Promise.reject(error)
    }
)

// 响应拦截器
service.interceptors.response.use(
    response => {
        const res = response.data
        // 从配置中获取是否需要全局错误提示，默认为 true
        const showError = response.config.showError !== false
        // 从配置中获取是否需要跳转登录，默认为 true，如果不需要 token 则为 false
        const needRedirect = response.config.needRedirect !== false && response.config.token !== false
        
        if (res.code === 200 || res.code === 0) {
            return res
        } else {
            // 错误提示（根据配置决定是否显示）
            if (showError) {
                Message({
                    message: res.msg || '请求失败',
                    type: 'error',
                    duration: 3000
                })
            }

            // 特殊错误码处理
            if (res.code === 401) {
                // 尝试刷新token
                return refreshToken().then(newToken => {
                    if (newToken) {
                        // 更新请求头并重新发送请求
                        response.config.headers['Authorization'] = newToken
                        return service(response.config)
                    } else {
                        if (needRedirect) {
                            Message({
                                message: '登录已过期，请重新登录',
                                type: 'warning',
                                duration: 2000
                            })
                            removeToken()
                            setTimeout(() => {
                                window.location.href = '/login'
                            }, 2000)
                        }
                        return Promise.reject(new Error(res.msg || '登录已过期'))
                    }
                })
            }

            return Promise.reject(new Error(res.msg || '请求失败'))
        }
    },
    error => {
        console.warn('响应错误')
        
        const showError = error.config?.showError !== false
        // 从配置中获取是否需要跳转登录，默认为 true，如果不需要 token 则为 false
        const needRedirect = error.config?.needRedirect !== false && error.config?.token !== false

        let message = '请求失败'

        if (error.response) {
            // 服务器返回了错误状态码
            switch (error.response.status) {
                case 400:
                    message = '请求参数错误'
                    break
                case 401:
                    // 尝试刷新token
                    return refreshToken().then(newToken => {
                        if (newToken) {
                            // 更新请求头并重新发送请求
                            error.config.headers['Authorization'] = newToken
                            return service(error.config)
                        } else {
                            message = '未授权，请重新登录'
                            if (needRedirect) {
                                removeToken()
                                setTimeout(() => {
                                    window.location.href = '/login'
                                }, 1500)
                            }
                            if (showError) {
                                Message({
                                    message: message,
                                    type: 'error',
                                    duration: 3000
                                })
                            }
                            return Promise.reject(error)
                        }
                    })
                case 403:
                    message = '拒绝访问'
                    break
                case 404:
                    message = '请求的资源不存在'
                    break
                case 500:
                    message = '服务器内部错误'
                    break
                case 502:
                    message = '网关错误'
                    break
                case 503:
                    message = '服务不可用'
                    break
                case 504:
                    message = '网关超时'
                    break
                default:
                    message = `连接错误 ${error.response.status}`
            }
        } else if (error.message) {
            if (error.message.includes('timeout')) {
                message = '请求超时'
            } else if (error.message.includes('Network')) {
                message = '网络连接异常'
            } else if (error.message.includes('请求过于频繁')) {
                message = error.message
            }
        }

        if (showError) {
            Message({
                message: message,
                type: 'error',
                duration: 3000
            })
        }

        return Promise.reject(error)
    }
)

// 封装请求方法
const request = {
    /**
     * GET 请求
     * @param {string} url - 请求地址
     * @param {object} params - 请求参数
     * @param {object} options - 其他配置项 (可以包含 token: false, showError: false, needRedirect: false, baseURL: string)
     * @returns {Promise}
     */
    get(url, params = {}, options = {}) {
        const { baseURL, ...restOptions } = options
        const config = {
            method: 'get',
            url: baseURL ? `${baseURL}${url}` : url,
            params,
            ...restOptions
        }
        return service(config)
    },

    /**
     * POST 请求
     * @param {string} url - 请求地址
     * @param {object} data - 请求体数据
     * @param {object} options - 其他配置项 (可以包含 token: false, showError: false, needRedirect: false, baseURL: string)
     * @returns {Promise}
     */
    post(url, data = {}, options = {}) {
        const { baseURL, ...restOptions } = options
        const config = {
            method: 'post',
            url: baseURL ? `${baseURL}${url}` : url,
            data,
            ...restOptions
        }
        return service(config)
    },

    /**
     * PUT 请求
     * @param {string} url - 请求地址
     * @param {object} data - 请求体数据
     * @param {object} options - 其他配置项 (可以包含 token: false, showError: false, needRedirect: false, baseURL: string)
     * @returns {Promise}
     */
    put(url, data = {}, options = {}) {
        const { baseURL, ...restOptions } = options
        const config = {
            method: 'put',
            url: baseURL ? `${baseURL}${url}` : url,
            data,
            ...restOptions
        }
        return service(config)
    },

    /**
     * DELETE 请求
     * @param {string} url - 请求地址
     * @param {object} params - 请求参数
     * @param {object} options - 其他配置项 (可以包含 token: false, showError: false, needRedirect: false, baseURL: string)
     * @returns {Promise}
     */
    delete(url, params = {}, options = {}) {
        const { baseURL, ...restOptions } = options
        const config = {
            method: 'delete',
            url: baseURL ? `${baseURL}${url}` : url,
            params,
            ...restOptions
        }
        return service(config)
    },

    /**
     * PATCH 请求
     * @param {string} url - 请求地址
     * @param {object} data - 请求体数据
     * @param {object} options - 其他配置项 (可以包含 token: false, showError: false, needRedirect: false, baseURL: string)
     * @returns {Promise}
     */
    patch(url, data = {}, options = {}) {
        const { baseURL, ...restOptions } = options
        const config = {
            method: 'patch',
            url: baseURL ? `${baseURL}${url}` : url,
            data,
            ...restOptions
        }
        return service(config)
    },

    /**
     * 上传文件
     * @param {string} url - 请求地址
     * @param {FormData} formData - 表单数据
     * @param {object} options - 其他配置项 (可以包含 token: false, showError: false, needRedirect: false, baseURL: string, onUploadProgress: function)
     * @returns {Promise}
     */
    upload(url, formData, options = {}) {
        const { baseURL, onUploadProgress, ...restOptions } = options
        const config = {
            method: 'post',
            url: baseURL ? `${baseURL}${url}` : url,
            data: formData,
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            onUploadProgress,
            ...restOptions
        }
        return service(config)
    }
}

// 导出 axios 实例和封装的请求方法
export default request
export { service }
